import React, { PropsWithChildren, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import clsx from 'clsx';
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	groupHeadingStyles,
	indicatorsContainerStyles,
	indicatorSeparatorStyles,
	loadingMessageStyles,
	menuPortalStyles,
	menuStyles,
	multiValueLabelStyles,
	multiValueRemoveStyles,
	multiValueStyles,
	noOptionsMessageStyles,
	optionStyles,
	placeholderStyles,
	selectInputStyles,
	singleValueStyles,
	valueContainerStyles,
} from '@app/component/form/input/autocomplete/AutocompleteStyles';
import { components } from 'react-select';
import { BuildingOffice2Icon } from '@heroicons/react/20/solid';
import SearchHotelResult from '@app/component/form/input/autocomplete/SearchHotelResult';
import AddNewHotelFromBookingUrlModal from '@app/component/form/input/autocomplete/AddNewHotelFromBookingUrlModal';
import Hotel from '@app/model/hotel/Hotel';
import HotelService from '@app/model/hotel/service/HotelService';
import SearchService from '@app/component/form/input/autocomplete/service/SearchService';
import Tag from '@app/component/tag/Tag';
import { useFieldContext } from '@app/site/_form';
import { FieldErrors } from '@app/site/_form/field/FieldErrors';
import useModal from '@app/component/overlays/modal/ModalRef';

type HotelFieldProps = {
	placeholder?: string,
	disabled?: boolean
}

export const HotelField:
	React.FunctionComponent<HotelFieldProps> = ({ placeholder, disabled }) => {

	const MIN_INPUT_LENGTH = 3;
	const modal = useModal();
	const [hotel, setHotel] = useState<SearchHotelResult | null>(null);
	const field = useFieldContext<number>();

	const hasError = field.state.meta.isTouched && !field.state.meta.isValid;

	useEffect(() => {
		if (!!field.state.value && field.state.value > 0) {
			HotelService.findHotelById(field.state.value).then(d => setHotel(mapHotel(d)));
		} else setHotel(null);
	}, [field.state.value]);


	const mapHotel = (hotel: Hotel): SearchHotelResult => {
		return {
			address: hotel.location?.city,
			countryName: hotel.location?.country.name,
			coordinates: hotel.location?.coordinates,
			id: hotel.id,
			name: hotel.name,
		};
	};

	const onCreateHotel = (hotel: Hotel) => {
		field.handleChange(hotel.id);
	};

	const formatOptionLabel = (hotel: SearchHotelResult) => (
		<div style={{ display: 'flex' }}>
			<div>{hotel.name}</div>
			<div style={{ marginLeft: '10px', color: '#ccc' }}>
				{(!!hotel.address ? hotel.address + ', ' : '') + hotel.countryName}
			</div>
		</div>
	);

	const ValueContainer: React.FunctionComponent<PropsWithChildren<any>> = ({ children, ...props }) => {
		const displayImport = !props.selectProps.isDisabled && !props.selectProps.value;
		const SelectValueContainer = components.ValueContainer as React.ComponentType<any>;

		return (
			<SelectValueContainer {...props}>
					{!!children && (
						<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
							<BuildingOffice2Icon className="h-5 w-5 text-slate-400" />
						</div>
					)}
					<div className="ml-8 flex">
						{children}
						{displayImport && <a onClick={() => modal.open()} className="hidden sm:block"><Tag state="info"
																																															 extraClassName="flex-right hover:drop-shadow-md">Can't
							find property?</Tag></a>}
					</div>
			</SelectValueContainer>
		);
	};

	const render = () => {
		return <AsyncSelect placeholder={placeholder}
												unstyled
												components={{ ValueContainer }}
												getOptionValue={(o: SearchHotelResult) => '' + o.id}
												getOptionLabel={(o: SearchHotelResult) => o.name}
												formatOptionLabel={formatOptionLabel}
												loadOptions={SearchService.searchHotels}
												value={hotel}
												isClearable={true}
												menuPortalTarget={document.body}
												onChange={(v: SearchHotelResult) => {
													field.handleChange(!!v ? v.id : 0);
												}}
												isDisabled={disabled}
												styles={{
													input: (base) => ({
														...base,
														'input:focus': {
															boxShadow: 'none',
														},
													}),
												}}
												noOptionsMessage={
													(input) => input.inputValue.length >= MIN_INPUT_LENGTH
														? 'Hotel not found'
														: 'Search input must be at least 3 characters'
												}
												classNames={{
													control: ({ isFocused }) =>
														clsx(
															isFocused ? controlStyles(hasError, disabled).focus : controlStyles(hasError, disabled).nonFocus,
															controlStyles(hasError, disabled).base,
														),
													placeholder: () => placeholderStyles(hasError),
													input: () => selectInputStyles,
													valueContainer: () => valueContainerStyles,
													singleValue: () => singleValueStyles,
													multiValue: () => multiValueStyles,
													multiValueLabel: () => multiValueLabelStyles,
													multiValueRemove: () => multiValueRemoveStyles,
													indicatorsContainer: () => indicatorsContainerStyles,
													clearIndicator: () => clearIndicatorStyles,
													indicatorSeparator: () => indicatorSeparatorStyles,
													dropdownIndicator: () => dropdownIndicatorStyles,
													menu: () => menuStyles,
													groupHeading: () => groupHeadingStyles,
													option: ({ isFocused, isSelected }) =>
														clsx(
															isFocused && optionStyles.focus,
															isSelected && optionStyles.selected,
															optionStyles.base,
														),
													noOptionsMessage: () => noOptionsMessageStyles,
													loadingMessage: () => loadingMessageStyles,
													menuPortal: () => menuPortalStyles,
												}}
		/>;
	};


	return <div id="hotel-autocomplete-wrapper" className="flex-grow">
		<div>
			{render()}
		</div>
		{!disabled &&
			<a className="font-semibold text-sm text-blue-600 hover:text-blue-500 cursor-pointer pl-2 block sm:hidden"
				 onClick={() => modal.open()}>Can't find property?</a>}
		<FieldErrors meta={field.state.meta} />
		<AddNewHotelFromBookingUrlModal onCreate={onCreateHotel} modal={modal} />
	</div>;
};
